import { useEffect, useState } from 'react'

import { AddEditAdditionalExpenses } from './components/modals/AddEditAdditionalExpenses'
import { AdditionalExpensesFilters } from './components/AdditionalExpensesFilters'
import { budgetPreviews } from './components/BudgetPreviews'
import { BudgetPreviewsWrapper } from '@components/shared/BudgetPreviewsWrapper'
import { Button } from '@components/shared/Button'
import { currencyMask } from '@utils/masks/currencyMask'
import { EditDetailedSummaryExpenses } from './components/modals/EditDetailedSummaryExpenses'
import { emotionTheme } from '@styles/theme'
import { getPagePermissions } from '@hooks/useUser/provider'
import { initialPagination, type PaginationParams } from '@components/shared/Table/components/Pagination/types'
import { PageIds } from '@hooks/useLayout/types'
import { PageWrapper } from '@components/shared/PageWrapper'
import { parseAdditionalExpensesGrid } from './helpers/parseAdditionalExpensesGrid'
import { parseBudgetPreviewsGrid } from './helpers/parseBudgetPreviewsGrid'
import { parseDetailedSummaryExpensesGrid } from './helpers/parseDetailedSummaryExpensesGrid'
import { PreviewWrapper } from '@components/shared/PreviewWrapper'
import { ReturnIcon } from '@assets/svgs/ReturnIcon'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowTableTotal2 } from '@components/shared/RowTableTotal2'
import { Slider } from '@components/shared/Slider'
import { Table } from '@components/shared/Table'
import { Title } from '@components/shared/Title'
import { TrashSimple } from '@assets/svgs/TrashSimple'
import { useMediaQuery } from '@mui/material'
import { useOtherExpenses } from './context'
import { useUser } from '@hooks/useUser'
// import { PROFILE_GU } from '@pages/Preview/helpers/previewGridValidationActions'
import { WarningBanner } from '@components/shared/WarningBanner'
import { WarningIcon } from '@phosphor-icons/react'

import type { AdditionalExpensesItemProps, DetailedSummaryExpensesItemProps } from './context/types'
import type { SortProps } from '@components/shared/Table/types'

import * as S from './styles'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'

export const OtherExpenses = () => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData, userPermissionsData } = useUser()
  // const isGUProfile = userPermissionsData?.profile?.name === PROFILE_GU

  const {
    additionalExpensesData,
    additionalExpensesLoading,
    getAdditionalExpenses,
    removeAdditionalExpense,
    isRemoveAdditionalExpenseLoading,
    getDetailedSummaryExpenses,
    isDetailedSummaryExpensesLoading,
    detailedSummaryExpensesData,
    getBigNumbersOtherExpenses,
    isBigNumbersOtherExpensesLoading,
    bigNumbersOtherExpensesData,
    approvalWorkflowOtherExpenses,
    isApprovalWorkflowOtherExpensesLoading,
    reversePreviewOtherExpenses,
    isReversePreviewOtherExpensesLoading,
    releasePreviewOtherExpenses,
    isReleasePreviewOtherExpensesLoading
  } = useOtherExpenses()

  const [additionalExpensesFormData, setAdditionalExpensesFormData] = useState<AdditionalExpensesItemProps | null>(null)
  const [openAddEditAdditionalExpensesModal, setOpenAddEditAdditionalExpensesModal] = useState(false)
  const [openDeleteAdditionalExpensesModal, setOpenDeleteAdditionalExpensesModal] = useState(false)
  const [detailedSummaryExpensesFormData, setDetailedSummaryExpensesFormData] =
    useState<DetailedSummaryExpensesItemProps | null>(null)
  const [openEditDetailedSummaryExpensesModal, setOpenEditDetailedSummaryExpensesModal] = useState(false)
  const [openApprovalWorkflowModal, setOpenApprovalWorkflowModal] = useState(false)
  const [openReleasePreviewModal, setOpenReleasePreviewModal] = useState(false)
  const [openReversePreviewModal, setOpenReversePreviewModal] = useState(false)
  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)
  const canEditFromPreview = bigNumbersOtherExpensesData?.canEdit === true
  const canEditPage = canEditFromPreview && permissions?.canEdit === true

  const handleOpenAddEditAdditionalExpenses = (data: AdditionalExpensesItemProps | null) => {
    setOpenAddEditAdditionalExpensesModal(true)
    setAdditionalExpensesFormData(data)
  }

  const handleOpenDeleteAdditionalExpenses = (data: AdditionalExpensesItemProps) => {
    setOpenDeleteAdditionalExpensesModal(true)
    setAdditionalExpensesFormData(data)
  }

  const handleOpenEditDetailedSummaryExpenses = (data: DetailedSummaryExpensesItemProps | null) => {
    setOpenEditDetailedSummaryExpensesModal(true)
    setDetailedSummaryExpensesFormData(data)
  }

  const handleGetDetailedSummaryExpenses = (
    params: PaginationParams | null,
    formData?: DetailedSummaryExpensesItemProps | null,
    sortData?: SortProps
  ) => {
    getDetailedSummaryExpenses({
      ...sortData,
      ...(formData || detailedSummaryExpensesFormData),
      filiCode: filiData?.filiCode!,
      competenceDate: filiData?.startPeriod!,
      page: params?.page || initialPagination.PAGE,
      itemsPerPage:
        params?.itemsPerPage || detailedSummaryExpensesData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })
    !!formData && setDetailedSummaryExpensesFormData(formData)
  }

  const handleGetAdditionalExpenses = (
    params: PaginationParams | null,
    formData?: AdditionalExpensesItemProps | null,
    sortData?: SortProps
  ) => {
    getAdditionalExpenses({
      ...sortData,
      ...(formData || additionalExpensesFormData),
      filiCode: filiData?.filiCode!,
      competenceDate: filiData?.startPeriod!,
      page: params?.page || initialPagination.PAGE,
      itemsPerPage: params?.itemsPerPage || additionalExpensesData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })
    !!formData && setAdditionalExpensesFormData(formData)
  }

  const handleInitialActionsFromScreen = () => {
    if (filiData?.filiCode && filiData?.filiCode !== 'ALL') {
      const payload = { filiCode: filiData?.filiCode!, competenceDate: filiData?.startPeriod! }

      handleGetAdditionalExpenses(null)
      handleGetDetailedSummaryExpenses(null)
      getBigNumbersOtherExpenses(payload)
    }
  }

  useEffect(() => {
    handleInitialActionsFromScreen()
  }, [filiData])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.OTHER_EXPENSES)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  return (
    <>
      <PageWrapper
        breadcrumbData={[
          { label: 'Gestão Financeira' },
          { label: `Outras Despesas / ${filiData?.filiCode} - ${filiData?.filiName}` }
        ]}
      >
        <WarningBanner message={bigNumbersOtherExpensesData?.corporateFoodSegmentMessage} />

        <Title label="Outras Despesas" />

        <BudgetPreviewsWrapper>
          {isMobile ? (
            <Slider items={budgetPreviews({ data: bigNumbersOtherExpensesData })} />
          ) : (
            <PreviewWrapper>{budgetPreviews({ data: bigNumbersOtherExpensesData })}</PreviewWrapper>
          )}

          <Table
            data={parseBudgetPreviewsGrid({ data: bigNumbersOtherExpensesData?.previews || [] })}
            isLoading={isBigNumbersOtherExpensesLoading}
          />
        </BudgetPreviewsWrapper>

        <Title label="Resumo Detalhado" marginBottom={8} />

        <AdditionalExpensesFilters
          isLoading={isDetailedSummaryExpensesLoading}
          data={detailedSummaryExpensesData?.availableFilters || null}
          onFilter={(formData) => handleGetDetailedSummaryExpenses(null, formData as DetailedSummaryExpensesItemProps)}
        />

        <Table
          data={parseDetailedSummaryExpensesGrid({
            data: detailedSummaryExpensesData?.items || [],
            onEdit: handleOpenEditDetailedSummaryExpenses,
            actionsDisabled: !canEditPage
          })}
          fixedRow={{
            element: (
              <RowTableTotal2
                data={[
                  {
                    label: 'Total Outras Despesas',
                    value: currencyMask(detailedSummaryExpensesData?.totalValue?.toFixed(2)) || 0
                  }
                ]}
              />
            )
          }}
          footer={{
            pagination: {
              currentPage: detailedSummaryExpensesData?.currentPage,
              itemsPerPage: detailedSummaryExpensesData?.itemsPerPage,
              totalItems: detailedSummaryExpensesData?.totalItems,
              totalPages: detailedSummaryExpensesData?.totalPages,
              onPageChange: (page) => handleGetDetailedSummaryExpenses({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetDetailedSummaryExpenses({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetDetailedSummaryExpenses(null, null, sortData)}
          isLoading={isDetailedSummaryExpensesLoading}
        />

        <Title label="Despesas Adicionais" marginBottom={8} />

        <AdditionalExpensesFilters
          isLoading={additionalExpensesLoading}
          data={additionalExpensesData?.availableFilters || null}
          onFilter={(formData) => handleGetAdditionalExpenses(null, formData as AdditionalExpensesItemProps)}
        />

        <Table
          data={parseAdditionalExpensesGrid({
            data: additionalExpensesData?.items || [],
            onEdit: handleOpenAddEditAdditionalExpenses,
            onDelete: handleOpenDeleteAdditionalExpenses,
            actionsDisabled: !canEditPage
          })}
          fixedRow={{
            element: (
              <RowTableTotal2
                data={[
                  {
                    label: 'Total Despesas Adicionais',
                    value: currencyMask(additionalExpensesData?.totalValue?.toFixed(2)) || 0
                  }
                ]}
              />
            )
          }}
          footer={{
            actionButton: {
              labelButton: 'Adicionar Despesas',
              onClick: () => handleOpenAddEditAdditionalExpenses(null),
              disabled: !canEditPage
            },
            pagination: {
              currentPage: additionalExpensesData?.currentPage,
              itemsPerPage: additionalExpensesData?.itemsPerPage,
              totalItems: additionalExpensesData?.totalItems,
              totalPages: additionalExpensesData?.totalPages,
              onPageChange: (page) => handleGetAdditionalExpenses({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetAdditionalExpenses({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetAdditionalExpenses(null, null, sortData)}
          isLoading={additionalExpensesLoading}
        />

        <S.Buttons>
          {bigNumbersOtherExpensesData?.allowRevertPreview && (
            <Button
              width={isMobile ? '100%' : 190}
              colorType="outlineBlue"
              onClick={() => setOpenReversePreviewModal(true)}
            >
              Reverter Prévia
            </Button>
          )}
        </S.Buttons>

        {canEditPage && (
          <S.Buttons>
            {/* <Tooltip
              width="fit-content"
              title="Esse botão foi adicionado para facilitar os testes, após a validação da tela ele será removido"
              placement="top"
            >
              <Button
                width={isMobile ? '100%' : 190}
                colorType="outlineBlue"
                onClick={() =>
                  reverseWorkflowOtherExpenses({
                    filiCode: filiData?.filiCode!,
                    competenceDate: filiData?.startPeriod!
                  })
                }
                disabled={
                  isReverseWorkflowOtherExpensesLoading ||
                  isBigNumbersOtherExpensesLoading ||
                  !(hasInProgressPreview || hasCompletedPreview)
                }
                loading={isReverseWorkflowOtherExpensesLoading}
              >
                Restaurar Prévia
              </Button>
            </Tooltip> */}

            {bigNumbersOtherExpensesData?.allowReleasePreview && (
              <Button
                width={isMobile ? '100%' : 190}
                colorType="solidBlue"
                onClick={() => setOpenReleasePreviewModal(true)}
              >
                Liberar Prévia
              </Button>
            )}
          </S.Buttons>
        )}
      </PageWrapper>

      <AddEditAdditionalExpenses
        data={additionalExpensesFormData}
        open={openAddEditAdditionalExpensesModal}
        onClose={() => setOpenAddEditAdditionalExpensesModal(false)}
        canEdit={canEditPage}
      />

      <EditDetailedSummaryExpenses
        data={detailedSummaryExpensesFormData}
        open={openEditDetailedSummaryExpensesModal}
        onClose={() => setOpenEditDetailedSummaryExpensesModal(false)}
        canEdit={canEditPage}
      />

      <ReturnModal
        open={openDeleteAdditionalExpensesModal}
        headerIcon={<TrashSimple />}
        title="Você tem certeza de que deseja excluir esta despesa??"
        headerTitle="Excluir Despesa?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenDeleteAdditionalExpensesModal}
        onConfirm={() => {
          if (!canEditPage) return

          removeAdditionalExpense({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!,
            expenseId: additionalExpensesFormData?.id!
          }).then((isSuccess) => {
            if (isSuccess) {
              setOpenDeleteAdditionalExpensesModal(false)
              setAdditionalExpensesFormData(null)
            }
          })
        }}
        isLoading={isRemoveAdditionalExpenseLoading}
      />

      <ReturnModal
        open={openApprovalWorkflowModal}
        headerIcon={<ReturnIcon />}
        headerTitle={`Prévia Outras Despesas - ${bigNumbersOtherExpensesData?.currentPreviewNumber}ª semana`}
        title="Você tem certeza de que deseja enviar a prévia?"
        message="A prévia será enviada para aprovação."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={() => setOpenApprovalWorkflowModal(false)}
        isLoading={isApprovalWorkflowOtherExpensesLoading}
        onConfirm={() =>
          approvalWorkflowOtherExpenses({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenApprovalWorkflowModal(false))
        }
      />

      <ReturnModal
        open={openReleasePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Liberar Prévia"
        message={`Tem certeza de que deseja liberar a ${bigNumbersOtherExpensesData?.currentPreviewNumber}ª Prévia de Outras Despesas?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReleasePreviewModal}
        isLoading={isReleasePreviewOtherExpensesLoading}
        onConfirm={() => {
          if (!canEditPage) return

          releasePreviewOtherExpenses({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReleasePreviewModal(false))
        }}
      />

      <ReturnModal
        open={openReversePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Reverter Prévia"
        message={`Tem certeza de que deseja reverter a ${bigNumbersOtherExpensesData?.currentPreviewNumber}ª Prévia de Outras Despesas?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReversePreviewModal}
        isLoading={isReversePreviewOtherExpensesLoading}
        onConfirm={() => {
          reversePreviewOtherExpenses({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReversePreviewModal(false))
        }}
      />
    </>
  )
}
